import { useEffect, useState } from 'react'

import * as Scrivito from 'scrivito'

import { DataClassName } from '@/Data/const'
import { SchemaAttribute, SchemaAttributes, SchemaRelation } from '@/Data/types'
import { getSchemaName } from '@/utils/dataClassMap'
import { getSchema } from '@/utils/getSchema'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

type LoadingState = 'idle' | 'loading' | 'success' | 'error'

const listObjectAttribute: SchemaAttribute = {
  type: 'enum',
  title: 'Object',
  localizedTitles: {
    de: 'Objekt',
    en: 'Object',
    fr: 'Objet',
    pl: 'Obiekt',
  },
  enumValues: [
    {
      title: 'Contacts',
      localizedTitles: {
        de: 'Kontakte',
        en: 'Contacts',
        fr: 'Contacts',
        pl: 'Kontakty',
      },
      value: DataClassName.PERSON,
    },
    {
      title: 'Companies',
      localizedTitles: {
        de: 'Unternehmen',
        en: 'Companies',
        fr: 'Entreprises',
        pl: 'Przedsiębiorstwa',
      },
      value: DataClassName.COMPANY,
    },
  ],
  hidden: false,
  maxLength: 0,
  mandatory: false,
  multiLine: false,
  readonly: false,
}

const withVirtualListAttributes = (
  entityName: string,
  attributes: SchemaAttributes | null,
): SchemaAttributes | null => {
  if (entityName.toLowerCase() !== DataClassName.LIST.toLowerCase()) {
    return attributes
  }

  return {
    ...(attributes || {}),
    entityType: listObjectAttribute,
  }
}

export const useEntitySchema = (entityName: string) => {
  const [attributes, setAttributes] = useState<SchemaAttributes>(
    {} as SchemaAttributes,
  )
  const [relations, setRelations] = useState<SchemaRelation[]>([])
  const [loadingState, setLoadingState] = useState<LoadingState>('loading')
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let isMounted = true

    const loadSchema = async () => {
      if (isMounted) {
        setLoadingState('loading')
      }

      try {
        const result = await Scrivito.load(() => {
          const { schema, error: schemaError } = getSchema(
            getSchemaName(entityName),
          )

          if (schemaError) {
            throw schemaError
          }

          if (!schema) {
            return { loadedAttributes: null, loadedRelations: null }
          }

          const loadedAttributes = getLocalizedSchemaAttributes(
            withVirtualListAttributes(
              entityName,
              schema.get('attributes') as SchemaAttributes,
            ) || undefined,
          ) as SchemaAttributes
          const rawRelations = schema.get('relations')
          const loadedRelations = Array.isArray(rawRelations)
            ? (rawRelations as SchemaRelation[])
            : []

          return { loadedAttributes, loadedRelations }
        })

        if (isMounted) {
          setAttributes(result.loadedAttributes || ({} as SchemaAttributes))
          setRelations(result.loadedRelations || [])
          setLoadingState('success')
          setError(null)
        }
      } catch (err) {
        if (isMounted) {
          console.error(`Error loading schema for ${entityName}:`, err)
          setAttributes({} as SchemaAttributes)
          setRelations([])
          setLoadingState('error')
          setError(err as Error)
        }
      }
    }

    loadSchema()

    return () => {
      isMounted = false
    }
  }, [entityName])

  return {
    attributes,
    relations,
    isLoading: loadingState === 'loading',
    error,
  }
}
